import ScDrawer from "@/PageComponents/Drawer/ScDrawer";
import {Flex, LoadingOverlay} from "@mantine/core";
import React, {FC, useEffect, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import ManagePurchaseOrder from "@/components/purchase/manage";

const PurchaseOrderDrawer: FC<{
    show?: boolean
    purchaseOrder: any
    onClose: () => void
    onSaved?: (result: any) => void
    accessStatus?: any
    /** create a new PO linked (and locked) to module/moduleID */
    isNew?: boolean
    module?: number
    moduleID?: string
    /** called with the created PO - the host should switch the drawer to it */
    onCreated?: (result: any) => void
    /** ID of the job/query/project whose page hosts the drawer (hides "Open Job" etc. for it) */
    hostItemID?: string
    /** options menu "New Purchase Order" - keeps it in the drawer instead of routing to the create page */
    onCreateNew?: () => void
    /** options menu "Copy Purchase Order" - the host should reopen the drawer with isNew and copyFromPurchaseOrder */
    onCopyToPurchaseOrder?: (purchaseOrder: any) => void
    copyFromPurchaseOrder?: any
}> = ({show, purchaseOrder: selectedPurchaseOrder, onClose, onSaved, accessStatus, isNew, module, moduleID, onCreated, hostItemID, onCreateNew, onCopyToPurchaseOrder, copyFromPurchaseOrder}) => {

    const [validateAndCloseCounter, setValidateAndClose] = useState(0)
    const [fullScreen, setFullScreen] = useState(false)

    const {data: purchaseOrder, isFetching} = useQuery(['purchaseOrder', selectedPurchaseOrder?.ID], () => Fetch.get({
        url: `/PurchaseOrder/${selectedPurchaseOrder?.ID}`
    }), {
        enabled: !!selectedPurchaseOrder?.ID && !isNew && show,
        staleTime: 0
    })

    const {data: company} = useQuery(['company'], () => Fetch.get({url: '/Company'}), {
        enabled: show
    })

    const drawerSize = 'clamp(900px, 100vw - 310px, 1320px)'

    const purchaseOrderNumber = purchaseOrder?.PurchaseOrderNumber ?? selectedPurchaseOrder?.PurchaseOrderNumber

    const prevTitleRef = useRef('');
    useEffect(() => {
        if (show) {
            prevTitleRef.current = document.title;
            return () => { document.title = prevTitleRef.current; };
        }
    }, [show]);
    useEffect(() => {
        if (!show) return;
        document.title = isNew
            ? 'Create Purchase Order | Purchases'
            : (purchaseOrderNumber ? `${purchaseOrderNumber} | Purchases` : 'Purchases | ServCraft');
    }, [show, purchaseOrderNumber, isNew]);

    // a copy is not linked to the host (matches /purchase/create?id=...), a new PO is
    const canRender = isNew ? (!!copyFromPurchaseOrder || (!!module && !!moduleID)) : (!isFetching && !!purchaseOrder?.ID)
    const linkToHost = isNew && !copyFromPurchaseOrder

    return <ScDrawer
        showFullscreenExpandButton
        fullscreen={fullScreen}
        onFullScreenModeChanges={setFullScreen}
        linkToFullPage={!isNew && !!selectedPurchaseOrder?.ID && ('/purchase/' + selectedPurchaseOrder.ID)}
        opened={!!show}
        onClose={() => {
            (isFetching && onClose()) ||
            setValidateAndClose((p: any) => p + 1)
        }}
        title={isNew ? 'Create Purchase Order' :
            <Flex gap={'sm'} align={'center'}>
                <span>Purchase Order {purchaseOrderNumber}</span>
            </Flex>
        }
        size={drawerSize}
        styles={{
            body: {
                paddingInlineEnd: 0
            },
            content: {
                overflowY: 'hidden'
            }
        }}
    >
        {
            show && canRender && !!company &&
            <ManagePurchaseOrder
                key={isNew ? `new-${copyFromPurchaseOrder?.ID ?? ''}` : purchaseOrder.ID}
                mode={'drawer'}
                isNew={!!isNew}
                module={linkToHost ? module : undefined}
                moduleID={linkToHost ? moduleID : undefined}
                copyFromPurchaseOrder={isNew ? copyFromPurchaseOrder : undefined}
                hostItemID={hostItemID}
                onCreateNew={onCreateNew}
                onCopyToPurchaseOrder={onCopyToPurchaseOrder}
                initTab={'Purchase'}
                purchaseOrder={isNew ? undefined : purchaseOrder}
                company={company}
                accessStatus={accessStatus}
                width={fullScreen ? 2000 : `calc(${drawerSize} - 30px)`}
                fullscreenMode={fullScreen}
                validateAndCloseCounter={validateAndCloseCounter}
                onClose={onClose}
                onSaved={onSaved}
                onCreated={isNew ? onCreated : undefined}
            />
        }

        <LoadingOverlay
            visible={isFetching} zIndex={1000} overlayProps={{radius: "sm", blur: 2}}
        />

    </ScDrawer>;

}

export default PurchaseOrderDrawer
